import type { ColorVariant } from "@agapi/ui/types";
import type { ColorToken, Token } from "@agapi/ui/panda/tokens";

/**
 * Converts a token string to a CSS variable format.
 * @param token - The token string to convert.
 * @returns The converted CSS variable string.
 */
export const token = (token: Token) => {
    const parsedToken = token.replace(/\./g, '-').replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    return `var(--${parsedToken})`;
}

export const colorToken = (color: ColorToken) => {
    return `var(--colors-${color.replace(/\./g, '-').replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()})`;
}

/**
 * Generates dynamic color styles based on the provided color variant.
 * @param colorVariant - The color variant to generate styles for.
 * @returns An object containing the background color and color styles.
 */
export const dynamicColorStyles = (colorVariant: ColorVariant) => ({
    backgroundColor: token(`colors.surface.${colorVariant}.background` as Token),
    color: token(`colors.surface.${colorVariant}.foreground` as Token)
});

export const dynamicColorString = (colorVariant: ColorVariant) => {
    return `background-color: ${dynamicColorStyles(colorVariant).backgroundColor}; color: ${dynamicColorStyles(colorVariant).color};`;
}

/**
 * Returns an array of color variant keys.
 * @returns An array of color variant keys.
 */
export const colorVariantKeys = () => {
    return [
        "default",
        "subdued",
        "contrast",
        "primary.subdued",
        "primary.tonal",
        "primary.vibrant",
        "primary.contrast",
        "secondary.subdued",
        "secondary.tonal",
        "secondary.vibrant",
        "secondary.contrast",
        "tertiary.subdued",
        "tertiary.tonal",
        "tertiary.vibrant",
        "tertiary.contrast",
        "commerce.campaign"
    ];
}